import { useEffect } from "react";
import type { SigningDocumentType } from "@aam/shared";

export interface SigningEmbedConfig {
  /** The `src` from Adobe Sign's `<script src="...">` embed snippet. */
  scriptSrc: string | null;
}

/**
 * Adobe sends these as a `<script src="...">` embed snippet. The `src` URL
 * itself serves a JS file (confirmed live: dropped into an `<iframe src>` it
 * just renders as raw script text, not a page) — the script's job is to
 * `document.write` a nested iframe pointing at the real signing widget once
 * it runs. `VITE_*_EMBED_URL` can override these per environment.
 */
const DEFAULT_ACH_FORM_SCRIPT_SRC =
  "https://na3.documents.adobe.com/public/embeddedWidget?wid=CBFCIBAA3AAABLblqZhCbhY-QqvNn2tf57Zh1HEyRWqD-4XBnooalhr0vIPK_3HLBj6PlRy3pwstO_1IuMVw*";
const DEFAULT_CC_AUTH_FORM_SCRIPT_SRC =
  "https://na3.documents.adobe.com/public/embeddedWidget?wid=CBFCIBAA3AAABLblqZhA3TIZEXr0VDso7wOeyt69d430213V4D5oJz80PLGUbsvwkD66ZgGNJwSUF8qN1Hdw*";

const SIGNING_EMBED_CONFIG: Record<SigningDocumentType, SigningEmbedConfig> = {
  ACH_FORM: { scriptSrc: import.meta.env.VITE_ACH_FORM_EMBED_URL || DEFAULT_ACH_FORM_SCRIPT_SRC },
  CC_AUTH_FORM: { scriptSrc: import.meta.env.VITE_CC_AUTH_FORM_EMBED_URL || DEFAULT_CC_AUTH_FORM_SCRIPT_SRC },
};

export function getSigningEmbedConfig(documentType: SigningDocumentType): SigningEmbedConfig {
  return SIGNING_EMBED_CONFIG[documentType];
}

export interface SigningPrefill {
  firstName?: string | null;
  lastName?: string | null;
}

/**
 * Adobe's `embeddedWidget` URL accepts `firstName` / `lastName` to prefill the
 * signer's name (see the empty `&firstName=&lastName=&nameEditable=true` params
 * Adobe's own script emits). We fill them from the signed-in agent so they
 * don't retype what we already know; `nameEditable=true` still lets them fix it.
 */
export function applySigningPrefill(scriptSrc: string, prefill: SigningPrefill): string {
  const trimmedFirst = prefill.firstName?.trim();
  const trimmedLast = prefill.lastName?.trim();
  if (!trimmedFirst && !trimmedLast) return scriptSrc;

  const params = new URLSearchParams();
  if (trimmedFirst) params.set("firstName", trimmedFirst);
  if (trimmedLast) params.set("lastName", trimmedLast);
  params.set("nameEditable", "true");

  const separator = scriptSrc.includes("?") ? "&" : "?";
  return `${scriptSrc}${separator}${params.toString()}`;
}

/**
 * Wraps Adobe's `<script src="...">` snippet in a bare HTML shell so it can
 * run inside its own fresh iframe document. This matters because the script
 * calls `document.write` to inject the actual signing iframe — calling
 * `document.write` on a document that has already finished loading wipes
 * the whole page instead of writing into it, so it can't run directly in our
 * app. Giving it a brand-new document (via the iframe's `srcDoc`) lets
 * `document.write` behave the way Adobe's snippet expects.
 */
export function buildSigningEmbedDocument(scriptSrc: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;padding:0;height:100%}</style></head><body><script src="${scriptSrc}"></script></body></html>`;
}

/**
 * The nested iframe Adobe's script writes can land on a related but
 * different host (e.g. secure.na3.echosign.com) than the embeddedWidget
 * script's own host (na3.documents.adobe.com) — so signing-complete
 * messages are trusted by domain family, not by an exact origin match.
 */
const TRUSTED_SIGNING_ORIGIN_SUFFIXES = ["adobe.com", "echosign.com"];

function isTrustedSigningOrigin(origin: string): boolean {
  try {
    const hostname = new URL(origin).hostname;
    return TRUSTED_SIGNING_ORIGIN_SUFFIXES.some(
      (suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`)
    );
  } catch {
    return false;
  }
}

/**
 * Signals that indicate the agreement was fully signed. Adobe's embedded
 * widget emits a few different shapes depending on flow, so we match on any of
 * these substrings (case-insensitively) against the stringified message:
 *  - `SIGNING_COMPLETE` / `AGREEMENT_COMPLETE` / `ESIGN_COMPLETE`: generic
 *    e-sign completion phrasings.
 *  - `"status":"SIGNED"` / `"state":"SIGNED"`: Adobe's structured status
 *    payloads once the signer finishes.
 *  - `THANKYOU` / `POST_SIGN`: the post-sign "thank you" transition Adobe
 *    fires when it would otherwise redirect to a success page.
 * `console.debug` logging (below) records the raw payload so the exact signal
 * can be confirmed from a real signature and this list tightened.
 */
const COMPLETION_KEYWORDS = [
  "SIGNING_COMPLETE",
  "AGREEMENT_COMPLETE",
  "ESIGN_COMPLETE",
  '"STATUS":"SIGNED"',
  '"STATE":"SIGNED"',
  "THANKYOU",
  "POST_SIGN",
];

export function isSigningCompleteMessage(data: unknown): boolean {
  const text = typeof data === "string" ? data : safeStringify(data);
  if (!text) return false;
  const upper = text.toUpperCase().replace(/\s+/g, "");
  return COMPLETION_KEYWORDS.some((keyword) => upper.includes(keyword));
}

function safeStringify(value: unknown): string | null {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

/**
 * Listens for a signing-complete postMessage from a trusted Adobe origin.
 * Every trusted-origin message is logged (debug level) so the real completion
 * payload can be identified from an actual signature — the widget is a public
 * embed with no server-side webhook, so this postMessage is our only automatic
 * completion signal (the manual "I've finished signing" button is the fallback).
 */
export function useSigningEmbedListener(enabled: boolean, onComplete: () => void) {
  useEffect(() => {
    if (!enabled) return;

    function handleMessage(event: MessageEvent) {
      if (!isTrustedSigningOrigin(event.origin)) return;
      // Diagnostic: record the raw shape so we can confirm/tighten COMPLETION_KEYWORDS.
      console.debug("[signing-embed] message from", event.origin, event.data);
      if (isSigningCompleteMessage(event.data)) onComplete();
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [enabled, onComplete]);
}
